/** Pure helpers for Workflow Templates page (unit-tested). */

export function templateUiKey(template) {
  if (!template) return '';
  return template.ui_key || template.template_id || '';
}

export function findTemplate(templates, uiKey) {
  return (templates || []).find((t) => templateUiKey(t) === uiKey);
}

export function findTemplateById(templates, templateId) {
  const needle = String(templateId || '').trim();
  if (!needle) return null;
  return (
    (templates || []).find((t) => String(t.template_id || '') === needle)
    || findTemplate(templates, needle)
  );
}

export function defaultBuiltinForAgent(templates, agent) {
  return (
    templates.find(
      (t) =>
        t.is_builtin
        && t.is_default
        && (t.agent || t.agent_kind) === agent,
    )
    || templates.find((t) => t.is_builtin && (t.agent || t.agent_kind) === agent)
  );
}

/** Recommended low-granularity starter (builtin `simple` template when present). */
export function simpleStarterForAgent(templates, agent) {
  const kind = String(agent || '').trim();
  if (!kind) return null;
  return (
    (templates || []).find(
      (t) =>
        t.is_builtin
        && String(t.template_id || '') === 'simple'
        && (t.agent || t.agent_kind) === kind,
    ) || null
  );
}

export function isStudioPaletteBlock(block) {
  if (!block) return false;
  const meta = block.metadata || {};
  return !meta.legacy && !meta.palette_hidden;
}

export function normalizeStep(step) {
  if (typeof step === 'string') {
    return { block_id: step, note: '', optional: false };
  }
  return {
    block_id: step.block_id || step.id || '',
    note: step.note || '',
    optional: Boolean(step.optional),
  };
}

export function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}

export function moveSteps(steps, index, direction) {
  const next = [...steps];
  const target = index + direction;
  if (target < 0 || target >= next.length) return steps;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** Insert a block at index (append when index === steps.length). */
export function insertStepAt(steps, index, blockId, partial = {}) {
  const step = normalizeStep({ block_id: blockId, ...partial });
  const next = [...steps];
  const at = Math.max(0, Math.min(index, next.length));
  next.splice(at, 0, step);
  return next;
}

/** Move an existing step from fromIndex to toIndex (insert-before semantics). */
export function reorderStep(steps, fromIndex, toIndex) {
  if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= steps.length) {
    return steps;
  }
  const next = [...steps];
  const [item] = next.splice(fromIndex, 1);
  let target = toIndex;
  if (fromIndex < toIndex) target -= 1;
  target = Math.max(0, Math.min(target, next.length));
  next.splice(target, 0, item);
  return next;
}

export const WORKFLOW_BLOCK_MIME = 'application/x-halo-workflow-block';
export const WORKFLOW_STEP_MIME = 'application/x-halo-workflow-step-index';

export function isReadOnlyBuiltinView({ isNew, selectedTemplate }) {
  return Boolean(!isNew && selectedTemplate?.is_builtin);
}
